import { useBlocker } from '@tanstack/react-router';
import { useEffect, useRef, type ReactNode } from 'react';
import { Button } from '../../../../components/Button';
import styles from './Evaluation.module.css';

/**
 * 06-qol §4: "unsaved changes warning when navigating away from a dirty form". These editors sit beside their list, so a
 * click on another rubric, kit or plan is a move inside the app — the browser's own warning never sees it (review R17). The
 * router holds that move until the person says whether to drop their changes; a full reload still gets the browser's.
 *
 * `release()` lets the next move through at once — after a save, before going to the version it made.
 */
export function useLeaveGuard(isDirty: boolean): { readonly prompt: ReactNode; readonly release: () => void } {
  const isDirtyRef = useRef(isDirty);
  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);
  const blocker = useBlocker({
    shouldBlockFn: ({ current, next }) => isDirtyRef.current && current.pathname !== next.pathname,
    enableBeforeUnload: () => isDirtyRef.current,
    withResolver: true,
  });
  const keepRef = useRef<HTMLButtonElement | null>(null);
  const isBlocked = blocker.status === 'blocked';
  useEffect(() => {
    if (isBlocked) keepRef.current?.focus();
  }, [isBlocked]);

  const prompt =
    blocker.status === 'blocked' ? (
      <div className={styles.leavePrompt} role="alertdialog" aria-label="Unsaved changes">
        <p className={styles.leaveText}>
          <strong>You have changes that aren't saved.</strong> Leave and drop them?
        </p>
        <div className={styles.leaveActions}>
          <Button ref={keepRef} size="sm" variant="ghost" onClick={blocker.reset}>
            Keep editing
          </Button>
          <Button size="sm" variant="danger" onClick={blocker.proceed}>
            Leave without saving
          </Button>
        </div>
      </div>
    ) : null;

  return {
    prompt,
    release: () => {
      isDirtyRef.current = false;
    },
  };
}
